import React, {useState} from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import styled from 'styled-components'
import SuccessDialog from 'lib/ui/Dialog/SuccessDialog'
import ErrorDialog from 'lib/ui/Dialog/ErrorDialog'
import {useDefaultPaymentMethod} from 'obvio/Billing/DefaultPaymentMethodProvider'
import {Section} from 'obvio/Billing'
import Subheading from 'lib/ui/typography/Subheading'
import {
  Subscription,
  useGetDelinquentSubscription,
} from 'obvio/Billing/subscribe'
import {getPlan} from 'obvio/Billing/plans'
import {api} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {TeamMember} from 'auth/user'
import {useObvioAuth} from 'obvio/auth'
import {usePastDueRecovery} from 'obvio/Billing/PastDueRecoveryProvider'
import {useBillingTransaction} from 'obvio/Billing/BillingTransactionProvider'

export default function PastDueSection() {
  const delinquentSubscription = useGetDelinquentSubscription()

  if (!delinquentSubscription) {
    return null
  }

  return <Content subscription={delinquentSubscription} />
}

function Content(props: {subscription: Subscription}) {
  const {subscription} = props
  const plan = subscription.plan ? getPlan(subscription.plan.name) : null
  const {setUser} = useObvioAuth()
  const {paymentMethod} = useDefaultPaymentMethod()
  const {openUpdateCardForm} = usePastDueRecovery()
  const {isTransactionPending, runTransaction} = useBillingTransaction()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [pendingUser, setPendingUser] = useState<TeamMember | null>(null)

  const statusLabel =
    subscription.stripe_status === 'unpaid' ? 'Unpaid' : 'Past due'

  const handleRetry = () => {
    if (isTransactionPending) {
      return
    }

    setErrorMessage(null)

    runTransaction(() =>
      teamMemberClient.post<TeamMember>(api('/subscribe/retry-payment')),
    )
      .then((updatedUser) => {
        setPendingUser(updatedUser)
        setSuccessMessage(
          'Payment successful! Your subscription is active again.',
        )
      })
      .catch((e) => {
        setErrorMessage(e.message || 'Unable to retry payment.')
      })
  }

  const handleCloseSuccessModal = () => {
    setSuccessMessage(null)

    if (pendingUser) {
      setUser(pendingUser)
      setPendingUser(null)
    }
  }

  const canRetryWithExistingCard = Boolean(paymentMethod)

  return (
    <Section>
      <SuccessDialog
        showing={Boolean(successMessage)}
        onClose={handleCloseSuccessModal}
      >
        {successMessage ?? ''}
      </SuccessDialog>
      <ErrorDialog
        showing={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        {errorMessage ?? ''}
      </ErrorDialog>
      <AlertContainer>
        <Subheading>Payment Required</Subheading>
        <Box mb={3}>
          <Typography>
            Your <strong>{plan?.label ?? 'Obvio'}</strong> subscription is{' '}
            <strong>{statusLabel.toLowerCase()}</strong>. Update your payment
            method or retry your current card to restore access.
          </Typography>
        </Box>
        <ActionButtons>
          <Button
            variant="contained"
            color="primary"
            disabled={isTransactionPending || !canRetryWithExistingCard}
            onClick={handleRetry}
            aria-label="retry payment"
          >
            Retry existing payment method
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={isTransactionPending}
            onClick={openUpdateCardForm}
            aria-label="update payment method"
          >
            Update payment method
          </Button>
        </ActionButtons>
      </AlertContainer>
      {!canRetryWithExistingCard && (
        <Box mb={3}>
          <Typography color="textSecondary">
            Add a default card below, or use Update payment method to pay with a
            new card.
          </Typography>
        </Box>
      )}
    </Section>
  )
}

const AlertContainer = styled.div`
  border: 1px solid ${(props) => props.theme.colors.error};
  border-radius: 16px;
  background-color: ${(props) => `${props.theme.colors.error}14`};
  padding: ${(props) => `${props.theme.spacing[7]} ${props.theme.spacing[4]}`};
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`
